'use client';

import React, { useState } from 'react';
import { Box, HStack, LucideIcon } from '@skin-seoul/design-system';
import { ProductType, useReviewService, type RecommendedProduct } from '@skin-seoul/service';
import { Carousel, type CarouselApi } from '@/components/ui/Carousel';
import DesktopProductCard from './cards/DesktopProductCard';
import { useProductCompareContext } from '../context/ProductCompareContext';
import AIChoiceProductCarouselSkeleton from './AIChoiceProductCarousel.skeleton';
import { trackAddToCartAICompare, trackAIChoiceCarousel } from '../utils/analytics';
interface AIChoiceProductCarouselProps {
  products?: RecommendedProduct[];
}
const AIChoiceProductCarousel = ({
  products
}: AIChoiceProductCarouselProps) => {
  const [api, setApi] = useState<CarouselApi>();
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);
  const {
    useGetReviewStatistics
  } = useReviewService();
  const {
    setSelectedProductId
  } = useProductCompareContext();
  const {
    data: reviewStats
  } = useGetReviewStatistics({
    productIds: products?.map(product => product.productId).join(',') || ''
  });
  const handleProductClick = (productLink: string) => {
    window.open(productLink, '_blank');
  };
  React.useEffect(() => {
    if (!api || !products) {
      return;
    }
    const updateScrollState = () => {
      setCanScrollPrev(api.canScrollPrev());
      setCanScrollNext(api.canScrollNext());
    };
    const handleSelect = () => {
      const selectedIndex = api.selectedScrollSnap();
      const product = products[selectedIndex];
      setSelectedProductId(prev => ({
        ...prev,
        recommendationProductId: product?.parentProductId ?? product?.productId
      }));
      updateScrollState();
    };
    api.on('select', handleSelect);
    handleSelect();
    return () => {
      api.off('select', handleSelect);
    };
  }, [api, products, setSelectedProductId]);
  const handlePrevClick = () => {
    api?.scrollPrev();
    trackAIChoiceCarousel('left');
  };
  const handleNextClick = () => {
    api?.scrollNext();
    trackAIChoiceCarousel('right');
  };
  if (!products || products.length === 0) {
    return null;
  }
  return <Box width="100%" display={{
    base: 'none',
    tablet: 'flex'
  }} flexDirection="column" gap={12} data-sentry-element="Box" data-sentry-component="AIChoiceProductCarousel" data-sentry-source-file="AIChoiceProductCarousel.tsx">
      <HStack position="relative" width="100%" justifyContent="space-between" data-sentry-element="HStack" data-sentry-source-file="AIChoiceProductCarousel.tsx">
        <LucideIcon color={canScrollPrev ? 'primary.400' : 'neutral.200'} name="ChevronLeft" boxSize={11} strokeWidth={1} onClick={canScrollPrev ? handlePrevClick : undefined} cursor={canScrollPrev ? 'pointer' : 'not-allowed'} opacity={canScrollPrev ? 1 : 0.5} data-sentry-element="LucideIcon" data-sentry-source-file="AIChoiceProductCarousel.tsx" />
        <Carousel setApi={setApi} opts={{
        loop: true,
        align: 'start',
        skipSnaps: false
      }} borderRadius="12px" boxShadow="0px 4px 16px 0px rgba(0, 0, 0, 0.12)" overflow="hidden" w="256px" data-sentry-element="Carousel" data-sentry-source-file="AIChoiceProductCarousel.tsx">
          <Carousel.Content data-sentry-element="Carousel.Content" data-sentry-source-file="AIChoiceProductCarousel.tsx">
            {products.map(product => {
            const efficacy = product.efficacy || [];
            const productReviewStats = reviewStats?.statistics.find(stat => stat.productId === product.productId);
            return <Carousel.Item key={product.sku} flexBasis={{
              base: '100%'
            }} minWidth="0" position="relative" backgroundColor="generic.0">
                  <DesktopProductCard sku={product.sku} href={product.productLink} name={product.name} brand={product.brand} productImage={product.productImageUrls[0]} currency={product.currency} originalPrice={product.originalPrice} salePrice={product.salePrice} productType={ProductType.Simple} rating={productReviewStats?.averageRate} reviewCount={productReviewStats?.totalReviews} efficacy={efficacy} onTrackAddToCart={() => trackAddToCartAICompare(product)} onClick={e => {
                e.preventDefault();
                handleProductClick(product.productLink);
              }} />
                </Carousel.Item>;
          })}
          </Carousel.Content>
        </Carousel>
        <LucideIcon color={canScrollNext ? 'primary.400' : 'neutral.200'} name="ChevronRight" boxSize={11} strokeWidth={1} onClick={canScrollNext ? handleNextClick : undefined} cursor={canScrollNext ? 'pointer' : 'not-allowed'} opacity={canScrollNext ? 1 : 0.5} data-sentry-element="LucideIcon" data-sentry-source-file="AIChoiceProductCarousel.tsx" />
      </HStack>
    </Box>;
};
AIChoiceProductCarousel.Skeleton = AIChoiceProductCarouselSkeleton;
export default AIChoiceProductCarousel;